<script setup lang="ts">
import { moduleShortcutFor, runCommand } from '../modules';
import { selectedObjects } from '../editor/selection';
import { computed, markRaw, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Editor, Renderer, type EditorHost } from '@vp/render';
import { CoreTypes } from '@vp/shared-types';
import { useApp } from '../stores/app';
import { t } from '../i18n';
import { logger, settings } from '../platform';
import { canvasDark } from '../theme';
import { ensureFontsFor } from '../fonts';
import { shortcutKey, VIEW_HISTORY_ON } from '@vp/render';
import { bumpDocRev, live, stats, syncHistory, syncViews, ui } from '../editor/bridge';
import { AttachmentImages } from '../editor/images';
import { installClipboard, pasteFromData } from '../editor/clipboard';
import { makeThumbnail, scheduleThumbnail } from '../editor/thumbnails';
import { moduleEditorHost, registerModuleTools } from '../modules';
import TopLeftBar from '../components/TopLeftBar.vue';
import TopRightBar from '../components/TopRightBar.vue';
import Toolbar from '../components/Toolbar.vue';
import PropertiesBar from '../components/PropertiesBar.vue';
import TreeCard from '../components/TreeCard.vue';
import RightSidebar from '../components/RightSidebar.vue';
import TextEditor from '../components/TextEditor.vue';
import ContextMenu from '../components/ContextMenu.vue';
import PerfPanel from '../components/PerfPanel.vue';
import ZoomControls from '../components/ZoomControls.vue';
import Minimap from '../components/Minimap.vue';
import SearchBar from '../components/SearchBar.vue';
import ShortcutsDialog from '../components/ShortcutsDialog.vue';
import ModuleViews from '../components/ModuleViews.vue';
import DiagramEditor from '../components/DiagramEditor.vue';
import CommandPalette from '../components/CommandPalette.vue';
import ObjectNotes from '../components/ObjectNotes.vue';
import AuthorPill from '../components/AuthorPill.vue';
import { profileCard } from '../profile-card';
import BookmarksCard from '../components/BookmarksCard.vue';
import LinkCard from '../components/LinkCard.vue';
import { linkPick } from '../editor/bookmarks';
import { makeComponent } from '../editor/components';
import ExportDialog from '../components/ExportDialog.vue';
import { copyStyle, painter, pasteStyle } from '../editor/style-painter';
import { dismissWindowOpen } from '../editor/dismiss';
import { dialogState } from '../dialogs';

const app = useApp();
const host = ref<HTMLDivElement | null>(null);
const ctxMenu = ref<{ x: number; y: number; target: string | null } | null>(null);
const exportingCanvas = ref<'png' | 'svg' | 'pdf' | null>(null);
let camRaf = 0;
const session = computed(() => (app.active ? app.sessions.get(app.active) ?? null : null));
let renderer: Renderer | null = null;
let images: AttachmentImages | null = null;
let editorUnsubs: (() => void)[] = [];
let uninstallClipboard: (() => void) | null = null;
let cameraSaveTimer: ReturnType<typeof setTimeout> | null = null;

const editorHost: EditorHost = {
  notify: (level, msg) => app.notify(level, msg),
  t,
  ...moduleEditorHost,
  preserveSelection: () => dismissWindowOpen(),
  objectAuthor: () => {
    const firstName = profileCard.firstName.trim();
    if (!profileCard.id || !firstName) return null;
    return { id: profileCard.id, firstName: firstName.slice(0, 80), lastName: profileCard.lastName.trim().slice(0, 80) };
  },
};

function detachEditor(): void {
  const ed = live.editor;
  if (ed && live.session) {
    live.session.selection = ed.selection;
    const c = renderer!.camera;
    live.session.setCamera({ cx: c.cx, cy: c.cy, zoom: c.zoom });
    if (ed.editing) ed.cancelTextEdit();
    void makeThumbnail(live.session);
  }
  for (const u of editorUnsubs) u();
  editorUnsubs = [];
  ed?.dispose();
  live.editor = null;
  live.session = null;
  ui.hover = null;
  syncViews();
}

function attachSession(): void {
  detachEditor();
  const s = session.value;
  if (!s || !renderer || !host.value) return;
  const ed = markRaw(new Editor(s.doc, s.history, s.scene, renderer, editorHost, settings.editor));
  ed.setPenStyle(settings.pen);
  ed.setPathStyle(settings.pathPen);
  live.editor = ed;
  live.session = s;
  ed.attach(host.value);
  editorUnsubs.push(registerModuleTools(ed));
  if (s.camera) renderer.camera.set(s.camera);
  else {
    renderer.camera.set({ cx: 0, cy: 0, zoom: 1 });
    if (s.doc.size) ed.fitAll({ record: false });
  }
  if (s.selection.length) ed.select(s.selection.filter((id) => s.doc.has(id)));
  ui.selection = ed.selection;
  ui.hover = null;
  ui.tool = ed.currentTool.id;
  ui.readOnly = s.isReadOnly;
  ui.zoom = renderer.camera.zoom;
  syncHistory();
  // canvas text does not make the browser load a web font: load the ones the objects use (redrawn when ready)
  void ensureFontsFor(s.doc.allObjects());
  editorUnsubs.push(
    ed.on('hover', (id) => {
      ui.hover = id;
    }),
    ed.on('selection', (sel) => {
      ui.selection = sel;
      if (ui.activeView && !sel.includes(ui.activeView)) ui.activeView = null;
      // the link and notes cards belong to the object: they close when it is no longer selected
      if (ui.linkFor && !sel.includes(ui.linkFor)) ui.linkFor = null;
      if (ui.notesFor && !sel.includes(ui.notesFor)) ui.notesFor = null;
    }),
    ed.on('activate', (id) => {
      const o = ed.doc.get(id);
      // diagrams open their code window; module live views become interactive
      if (o && o.type === CoreTypes.diagram) ui.diagramEdit = { id, source: (o.props['source'] as string) ?? '' };
      else ui.activeView = id;
    }),
    ed.on('tool', (id) => {
      ui.tool = id;
      ui.toolsRev++;
    }),
    ed.on('history', syncHistory),
    ed.on('view', syncViews),
    ed.on('textedit', (id) => (ui.editingText = id)),
    ed.on('pathedit', (id) => (ui.pathEditing = id)),
    ed.on('contextmenu', (m) => (ctxMenu.value = m)),
    s.doc.onChange((ev) => {
      bumpDocRev();
      const touched = [...ev.created, ...ev.updated].map((id) => s.doc.get(id)).filter((o) => !!o && !!o.style.text);
      if (touched.length) void ensureFontsFor(touched as NonNullable<(typeof touched)[number]>[]);
      if (ev.origin === 'local') scheduleThumbnail(s);
      ui.readOnly = s.isReadOnly;
    }),
    renderer.camera.onChange(() => {
      ui.zoom = renderer!.camera.zoom;
      if (!camRaf)
        camRaf = requestAnimationFrame(() => {
          camRaf = 0;
          ui.camRev++;
        });
      if (cameraSaveTimer) clearTimeout(cameraSaveTimer);
      cameraSaveTimer = setTimeout(() => {
        const c = renderer!.camera;
        live.session?.setCamera({ cx: c.cx, cy: c.cy, zoom: c.zoom });
      }, 800);
    }),
  );
  const unwatchLock = watch(s.lockState, () => {
    ui.readOnly = s.isReadOnly;
    if (s.isReadOnly && ed.currentTool.kind !== 'select' && ed.currentTool.kind !== 'hand') ed.setTool('select');
    renderer?.invalidate();
  });
  const unwatchLoading = watch(s.loading, (l) => {
    ui.readOnly = s.isReadOnly;
    if (!l && !s.camera && s.doc.size) ed.fitAll({ record: false });
  });
  editorUnsubs.push(unwatchLock, unwatchLoading);
  ui.toolsRev++;
  syncViews();
  host.value.focus({ preventScroll: true });
  renderer.invalidateScene();
}

onMounted(() => {
  if (!host.value) return;
  images = new AttachmentImages(() => renderer?.invalidateScene());
  renderer = markRaw(
    new Renderer(host.value, {
      preferred: settings.renderer,
      background: settings.background,
      darkInk: canvasDark.value,
      images,
      textLodPx: settings.textLodPx,
      onBackendChange: (kind, reason) => {
        ui.backend = kind;
        logger.info('render.backend', { backend: kind, reason: reason.split(':')[0] });
        if (kind === 'canvas2d' && settings.renderer === 'webgl2' && reason !== 'initial' && reason !== 'user') app.notify('warning', t('error.webgl'));
      },
    }),
  );
  live.renderer = renderer;
  renderer.onStats((s) => {
    if (settings.perfPanel) stats.value = s;
  });
  uninstallClipboard = installClipboard();
  attachSession();
});

/**
 * Editor-wide shortcuts that are not canvas commands: find (Ctrl+F), the command palette (Ctrl+K),
 * copy / paste style (Ctrl+Alt+C / V), notes of an object (Ctrl+Alt+M),
 * Zen (`Z`, panels hide, the canvas stays) and the shortcut reference (?).
 * Previous/next view (Alt+Left/Right) is OFF (`VIEW_HISTORY_ON`).
 */
function typingTarget(e: KeyboardEvent): boolean {
  const el = e.target as HTMLElement | null;
  return !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
}

/** A floating window that is actually on screen (Zen hides the shell, those windows must not keep Esc). */
function visibleDismiss(): boolean {
  if (typeof document === 'undefined') return false;
  for (const el of document.querySelectorAll('[data-vp-dismiss]')) {
    if (el instanceof HTMLElement && el.getClientRects().length > 0) return true;
  }
  return false;
}
function onZenEscape(e: KeyboardEvent): void {
  if (!ui.zen || app.view !== 'editor' || e.key !== 'Escape' || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
  if (typingTarget(e)) return;
  if (ui.dragging || ui.editingText || ui.pathEditing || live.editor?.pathDraftState) return;
  if (ui.paletteOpen || ui.keysOpen || ui.diagramEdit || ctxMenu.value || exportingCanvas.value || dialogState.current || visibleDismiss()) return;
  e.preventDefault();
  e.stopPropagation();
  ui.zen = false;
}

function onGlobalKey(e: KeyboardEvent): void {
  if (app.view !== 'editor') return;
  const mod = e.ctrlKey || e.metaKey;
  const key = shortcutKey(e);
  if (mod && key === 'f') {
    e.preventDefault();
    ui.searchOpen = true;
    return;
  }
  if (mod && !e.altKey && !e.shiftKey && key === 'k') {
    e.preventDefault();
    ui.paletteOpen = !ui.paletteOpen;
    return;
  }
  const typing0 = typingTarget(e);
  if (mod && e.altKey && !e.shiftKey && !typing0 && key === 'k') {
    e.preventDefault();
    if (!ui.readOnly && ui.selection.length) void makeComponent();
    return;
  }
  if (mod && e.altKey && !e.shiftKey && !typing0 && (key === 'c' || key === 'v' || key === 'm')) {
    e.preventDefault();
    if (key === 'c') {
      if (copyStyle()) app.notify('info', t('palette.styleCopied'), 2000);
    } else if (key === 'v') pasteStyle();
    else if (ui.selection.length === 1) ui.notesFor = ui.notesFor ? null : ui.selection[0];
    return;
  }
  // Ctrl+S: the browser would offer to save the page; here it saves the canvas right now
  if (mod && !e.shiftKey && key === 's') {
    e.preventDefault();
    const s = live.session;
    if (s) {
      void s.flush().then(() => app.notify(s.saveStatus.value === 'error' ? 'error' : 'info', s.saveStatus.value === 'error' ? t('canvas.saveError') : t('keys.saved'), 2500));
    }
    return;
  }
  const typing = typingTarget(e);
  // The canvas handler stops the event when it has focus; this covers the rest of the editor.
  if (VIEW_HISTORY_ON && !typing && e.altKey && !mod && !e.shiftKey && (key === 'arrowleft' || key === 'arrowright')) {
    e.preventDefault();
    if (key === 'arrowleft') live.editor?.backView();
    else live.editor?.forwardView();
    return;
  }
  if (!e.repeat && !e.defaultPrevented && !mod && !e.altKey && !e.shiftKey && !typing && key === 'z' && !ui.paletteOpen && !ui.keysOpen && !ui.diagramEdit && !ctxMenu.value && !exportingCanvas.value && !dialogState.current) {
    e.preventDefault();
    ui.zen = !ui.zen;
    return;
  }
  if (e.key === '?' && !typing) {
    e.preventDefault();
    ui.keysOpen = true;
    return;
  }
  // API 2.2: shortcuts of modules, after the platform's own (those prevent the default)
  if (!typing && !e.defaultPrevented && !ui.readOnly) {
    const hit = moduleShortcutFor(e, key, selectedObjects.value);
    if (hit) {
      e.preventDefault();
      void runCommand(hit.moduleId, hit.command);
    }
  }
}

function closeSearch(): void {
  ui.searchOpen = false;
  host.value?.focus({ preventScroll: true });
}

onMounted(() => {
  window.addEventListener('keydown', onGlobalKey);
  window.addEventListener('keydown', onZenEscape, true);
});

function openNotes(): void {
  if (ui.selection.length === 1) ui.notesFor = ui.selection[0];
}

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalKey);
  window.removeEventListener('keydown', onZenEscape, true);
  ui.zen = false;
  if (camRaf) cancelAnimationFrame(camRaf);
  ui.paletteOpen = false;
  ui.notesFor = null;
  ui.linkFor = null;
  ui.bookmarksOpen = false;
  linkPick.from = null;
  painter.armed = false;
  detachEditor();
  uninstallClipboard?.();
  renderer?.dispose();
  images?.clear();
  live.renderer = null;
});

watch(session, () => attachSession());
watch(
  () => settings.editor,
  (s) => live.editor?.setSettings(s),
  { deep: true },
);
watch(
  () => settings.pen,
  (s) => live.editor?.setPenStyle(s),
  { deep: true },
);
watch(
  () => settings.pathPen,
  (s) => live.editor?.setPathStyle(s),
  { deep: true },
);
watch(
  () => settings.renderer,
  (r) => renderer?.setBackend(r),
);
watch(
  () => settings.background,
  (b) => renderer?.setBackground(b),
);
watch(canvasDark, (on) => renderer?.setDarkInk(on));
watch(
  () => settings.textLodPx,
  (v) => {
    if (renderer) {
      renderer.opts.textLodPx = v;
      renderer.invalidateScene();
    }
  },
);

watch(
  () => app.view,
  (v) => {
    if (v !== 'editor') ui.zen = false;
  },
);
watch(
  () => ui.zen,
  (on) => {
    if (!on || app.view !== 'editor') return;
    app.drawerOpen = false;
    ui.searchOpen = false;
    ui.bookmarksOpen = false;
    ui.linkFor = null;
    ui.notesFor = null;
    linkPick.from = null;
    painter.armed = false;
    host.value?.focus({ preventScroll: true });
  },
);

function onDrop(e: DragEvent): void {
  e.preventDefault();
  if (!e.dataTransfer || ui.readOnly) return;
  void pasteFromData(e.dataTransfer);
}

async function takeOver(): Promise<void> {
  await session.value?.takeOver();
}
</script>

<template>
  <div class="editor" :class="{ zen: ui.zen }">
    <div class="stage" :style="{ '--bottom-reserve': ui.zen ? '12px' : settings.minimap ? '216px' : '64px' }">
      <div
        ref="host"
        class="canvas-host"
        role="application"
        :aria-label="session?.meta.value.name ?? ''"
        @dragover.prevent
        @drop="onDrop"
        @pointerdown="ui.dragging = true"
        @pointerup="ui.dragging = false"
        @pointercancel="ui.dragging = false"
      ></div>
      <ModuleViews />
      <ObjectNotes />
      <TextEditor />
      <SearchBar v-if="ui.searchOpen" @close="closeSearch" />
      <BookmarksCard v-if="ui.bookmarksOpen" @close="ui.bookmarksOpen = false" />
      <LinkCard v-if="ui.linkFor" :id="ui.linkFor" @close="ui.linkFor = null" />
      <div v-if="linkPick.from" class="banner float pillbar painter-hint" role="status">
        <span>{{ t('links.pickHint') }}</span>
        <button class="pill" @click="linkPick.from = null">{{ t('common.cancel') }}</button>
      </div>
      <Toolbar />
      <p v-if="ui.zen" class="zen-live" role="status">{{ t('zen.on') }}</p>
      <div class="shell">
        <AuthorPill />
        <TopLeftBar />
        <TopRightBar />
        <TreeCard v-if="settings.treeOpen" />
        <PropertiesBar />
        <RightSidebar />
        <div v-if="painter.armed" class="banner float pillbar painter-hint" role="status">
          <span>{{ t('palette.brushHint') }}</span>
          <button class="pill" @click="painter.armed = false">{{ t('common.cancel') }}</button>
        </div>
        <Minimap />
        <ZoomControls />
        <PerfPanel v-if="settings.perfPanel" />
      </div>
      <div v-if="session?.loading.value" class="banner float pillbar">{{ t('canvas.loading', { n: session.loadedCount.value }) }}</div>
      <div v-else-if="session && session.lockState.value === 'readonly'" class="banner float pillbar warn">
        <span>{{ t('canvas.readOnly') }}</span>
        <button class="pill active" @click="takeOver">{{ t('canvas.takeOver') }}</button>
      </div>
    </div>
    <ShortcutsDialog v-if="ui.keysOpen" @close="ui.keysOpen = false" />
    <CommandPalette v-if="ui.paletteOpen" @close="ui.paletteOpen = false" @export="(f) => (exportingCanvas = f)" @notes="openNotes" />
    <ExportDialog v-if="exportingCanvas" :format="exportingCanvas" initial-scope="all" @close="exportingCanvas = null" />
    <DiagramEditor v-if="ui.diagramEdit" />
    <ContextMenu v-if="ctxMenu" :x="ctxMenu.x" :y="ctxMenu.y" :target="ctxMenu.target" @close="ctxMenu = null" />
  </div>
</template>

<style scoped>
.editor {
  height: 100%;
}
.stage {
  position: relative;
  height: 100%;
  overflow: hidden;
  /* not a scroll container: focusing something outside the screen (a big object being edited) must not slide the interface */
  overflow: clip;
}
.canvas-host {
  position: absolute;
  inset: 0;
  outline: none;
  touch-action: none;
}
.shell {
  display: contents;
}
.editor.zen .shell {
  display: none;
}
.zen-live {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  border: 0;
}
.banner {
  top: 64px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 4px 4px 14px;
  gap: 10px;
}
.banner.warn {
  background: var(--warn-soft);
}
.painter-hint {
  top: auto;
  bottom: 72px;
}
</style>
